import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
  FiCheck, FiX, FiZap, FiArrowRight,
  FiShield, FiUsers, FiFileText, FiLayers,
  FiActivity, FiCheckCircle, FiStar
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI } from '../services/api';
import BackgroundShapes from '../components/common/BackgroundShapes';
import Footer from '../components/common/Footer';
import SEO from '../components/common/SEO';
import logo from '../assets/logo.png';
import '../styles/pricing.css';

const Pricing = () => {
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const sessionComplete = searchParams.get('session') === 'complete';

  const handleUpgrade = async () => {
    if (!user) {
      window.location.href = '/register';
      return;
    }
    setLoadingCheckout(true);
    try {
      const returnUrl = `${window.location.origin}/pricing?session=complete`;
      const response = await subscriptionAPI.createCheckout(returnUrl);
      if (response.data.payment_link) {
        window.location.href = response.data.payment_link;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start checkout';
      alert(msg);
    } finally {
      setLoadingCheckout(false);
    }
  };

  const freePlan = {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Perfect for individuals and small teams getting started with compliance management.',
    features: [
      { text: '1 Workspace', included: true },
      { text: '10 Change Requests / month', included: true },
      { text: '10 Templates', included: true },
      { text: 'Status Workflow', included: true },
      { text: 'Activity Logs', included: true },
      { text: 'Team Collaboration', included: true },
      { text: 'Unlimited Workspaces', included: false },
      { text: 'Unlimited Change Requests', included: false },
      { text: 'Unlimited Templates', included: false },
      { text: 'Priority Email Support', included: false },
      { text: 'Advanced Audit Reports', included: false },
      { text: 'Custom Integrations', included: false },
    ],
  };

  const premiumPlan = {
    name: 'Premium',
    price: '$40',
    period: '/month',
    description: 'For growing teams that need unlimited capacity and advanced features.',
    features: [
      { text: 'Unlimited Workspaces', included: true },
      { text: 'Unlimited Change Requests', included: true },
      { text: 'Unlimited Templates', included: true },
      { text: 'Status Workflow', included: true },
      { text: 'Activity Logs', included: true },
      { text: 'Team Collaboration', included: true },
      { text: 'Priority Email Support', included: true },
      { text: 'Advanced Audit Reports', included: true },
      { text: 'Custom Integrations', included: true },
      { text: 'Role-based Access Control', included: true },
      { text: 'Export to Excel', included: true },
      { text: 'Dedicated Account Manager', included: true },
    ],
  };

  const faqs = [
    {
      q: 'Can I switch plans at any time?',
      a: 'Yes, you can upgrade from Free to Premium at any time. Your billing will start immediately.',
    },
    {
      q: 'What happens if I exceed the free plan limits?',
      a: 'You\'ll receive a notification asking you to upgrade. Your existing data is never deleted.',
    },
    {
      q: 'Is there a long-term contract?',
      a: 'No, the Premium plan is billed monthly and you can cancel anytime. No contracts or hidden fees.',
    },
    {
      q: 'Do you offer refunds?',
      a: 'Yes, we offer a full refund within the first 14 days if you\'re not satisfied. See our refund policy for details.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  const isCurrentPlan = (planName) => {
    if (!user) return false;
    return (user.plan || 'free') === planName.toLowerCase();
  };

  return (
    <div className="landing-page">
      <SEO
        title="Pricing - ChangeFox"
        description="Simple, transparent pricing for ChangeFox. Start free, upgrade when you need more."
        path="/pricing"
      />
      <BackgroundShapes />

      {/* Navigation */}
      <motion.nav
        className="landing-nav"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Container>
          <div className="nav-content">
            <Link to="/" className="nav-logo">
              <img src={logo} alt="ChangeFox" className="nav-logo-img" />
            </Link>
            <div className="nav-links">
              {user ? (
                <>
                  <Link to="/dashboard" className="nav-link">Dashboard</Link>
                  <Link to="/pricing" className="nav-link">Pricing</Link>
                  <button onClick={() => { logout(); window.location.href = '/login'; }} className="btn-nav-signout">
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/" className="nav-link">Home</Link>
                  <Link to="/pricing" className="nav-link">Pricing</Link>
                  <Link to="/login" className="nav-link">Sign In</Link>
                  <Link to="/register">
                    <Button className="btn-nav-cta">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </Container>
      </motion.nav>

      {/* Success Banner */}
      {sessionComplete && (
        <motion.div
          className="pricing-success-banner"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Container>
            <div className="success-banner-content">
              <FiCheckCircle size={24} />
              <div>
                <strong>Payment successful!</strong> Your Premium plan is now being activated. It may take a moment to reflect.
              </div>
              <Link to="/dashboard">
                <Button className="btn-success-dashboard">Go to Dashboard</Button>
              </Link>
            </div>
          </Container>
        </motion.div>
      )}

      {/* Hero */}
      <section className="pricing-hero">
        <Container>
          <motion.div
            className="pricing-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="section-badge">Pricing</div>
            <h1 className="pricing-hero-title">
              Simple, transparent
              <span className="gradient-text"> pricing</span>
            </h1>
            <p className="pricing-hero-subtitle">
              Start for free, upgrade when you need more. No hidden fees, no surprises.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Pricing Cards */}
      <section className="pricing-cards-section">
        <Container>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Row className="justify-content-center g-4">
              {/* Free Plan */}
              <Col lg={5} md={6}>
                <motion.div className="pricing-card" variants={itemVariants}>
                  <div className="pricing-card-header">
                    <div className="plan-icon free-icon">
                      <FiLayers />
                    </div>
                    <h3 className="plan-name">{freePlan.name}</h3>
                    <div className="plan-price">
                      <span className="price-amount">{freePlan.price}</span>
                      <span className="price-period">{freePlan.period}</span>
                    </div>
                    <p className="plan-description">{freePlan.description}</p>
                  </div>
                  <div className="pricing-card-body">
                    <ul className="feature-list">
                      {freePlan.features.map((f, i) => (
                        <li key={i} className={f.included ? 'included' : 'excluded'}>
                          {f.included ? <FiCheck className="feature-check" /> : <FiX className="feature-x" />}
                          <span>{f.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="pricing-card-footer">
                    {isCurrentPlan('Free') ? (
                      <Button className="btn-plan-current" disabled>Current Plan</Button>
                    ) : user ? (
                      <Button className="btn-plan-current" disabled>Current Plan</Button>
                    ) : (
                      <Link to="/register" style={{ width: '100%' }}>
                        <Button className="btn-plan-free">Get Started Free</Button>
                      </Link>
                    )}
                  </div>
                </motion.div>
              </Col>

              {/* Premium Plan */}
              <Col lg={5} md={6}>
                <motion.div className="pricing-card premium" variants={itemVariants}>
                  <div className="popular-badge">
                    <FiStar /> Most Popular
                  </div>
                  <div className="pricing-card-header">
                    <div className="plan-icon premium-icon">
                      <FiZap />
                    </div>
                    <h3 className="plan-name">{premiumPlan.name}</h3>
                    <div className="plan-price">
                      <span className="price-amount">{premiumPlan.price}</span>
                      <span className="price-period">{premiumPlan.period}</span>
                    </div>
                    <p className="plan-description">{premiumPlan.description}</p>
                  </div>
                  <div className="pricing-card-body">
                    <ul className="feature-list">
                      {premiumPlan.features.map((f, i) => (
                        <li key={i} className={f.included ? 'included' : 'excluded'}>
                          {f.included ? <FiCheck className="feature-check" /> : <FiX className="feature-x" />}
                          <span>{f.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="pricing-card-footer">
                    {isCurrentPlan('Premium') ? (
                      <Button className="btn-plan-current" disabled>Current Plan</Button>
                    ) : (
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ width: '100%' }}>
                        <Button
                          className="btn-plan-premium"
                          onClick={handleUpgrade}
                          disabled={loadingCheckout}
                        >
                          {loadingCheckout ? 'Redirecting...' : 'Upgrade to Premium'}
                          {!loadingCheckout && <FiArrowRight className="ms-2" />}
                        </Button>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Feature Comparison */}
      <section className="comparison-section">
        <Container>
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">
              Feature <span className="gradient-text">comparison</span>
            </h2>
          </motion.div>

          <motion.div
            className="comparison-table-wrapper"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free</th>
                  <th className="premium-col">Premium</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><FiLayers className="me-2" /> Workspaces</td>
                  <td>1</td>
                  <td className="premium-col">Unlimited</td>
                </tr>
                <tr>
                  <td><FiFileText className="me-2" /> Change Requests</td>
                  <td>10 / month</td>
                  <td className="premium-col">Unlimited</td>
                </tr>
                <tr>
                  <td><FiFileText className="me-2" /> Templates</td>
                  <td>10</td>
                  <td className="premium-col">Unlimited</td>
                </tr>
                <tr>
                  <td><FiUsers className="me-2" /> Team Members</td>
                  <td>Unlimited</td>
                  <td className="premium-col">Unlimited</td>
                </tr>
                <tr>
                  <td><FiActivity className="me-2" /> Activity Logs</td>
                  <td><FiCheck className="text-success" /></td>
                  <td className="premium-col"><FiCheck className="text-success" /></td>
                </tr>
                <tr>
                  <td><FiShield className="me-2" /> Role-based Access</td>
                  <td><FiCheck className="text-success" /></td>
                  <td className="premium-col"><FiCheck className="text-success" /></td>
                </tr>
                <tr>
                  <td><FiZap className="me-2" /> Priority Support</td>
                  <td><FiX className="text-muted" /></td>
                  <td className="premium-col"><FiCheck className="text-success" /></td>
                </tr>
                <tr>
                  <td><FiFileText className="me-2" /> Export to Excel</td>
                  <td><FiX className="text-muted" /></td>
                  <td className="premium-col"><FiCheck className="text-success" /></td>
                </tr>
                <tr>
                  <td><FiActivity className="me-2" /> Advanced Audit Reports</td>
                  <td><FiX className="text-muted" /></td>
                  <td className="premium-col"><FiCheck className="text-success" /></td>
                </tr>
                <tr>
                  <td><FiLayers className="me-2" /> Custom Integrations</td>
                  <td><FiX className="text-muted" /></td>
                  <td className="premium-col"><FiCheck className="text-success" /></td>
                </tr>
              </tbody>
            </table>
          </motion.div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <Container>
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">
              Frequently asked <span className="gradient-text">questions</span>
            </h2>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <Row className="justify-content-center">
              <Col lg={8}>
                {faqs.map((faq, i) => (
                  <motion.div key={i} className="faq-item" variants={itemVariants}>
                    <h4 className="faq-question">{faq.q}</h4>
                    <p className="faq-answer">{faq.a}</p>
                  </motion.div>
                ))}
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <Container>
          <motion.div
            className="cta-card"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="cta-content">
              <h2 className="cta-title">
                Ready to unlock
                <br />
                <span className="gradient-text">unlimited potential?</span>
              </h2>
              <p className="cta-subtitle">
                Upgrade to Premium and remove all limits on your compliance workflow.
              </p>
              <div className="cta-buttons">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button className="btn-cta-primary" onClick={handleUpgrade} disabled={loadingCheckout}>
                    {loadingCheckout ? 'Redirecting...' : 'Upgrade Now'}
                    {!loadingCheckout && <FiArrowRight className="ms-2" />}
                  </Button>
                </motion.div>
              </div>
              <div className="cta-features">
                <div className="cta-feature">
                  <FiCheckCircle />
                  <span>Cancel anytime</span>
                </div>
                <div className="cta-feature">
                  <FiCheckCircle />
                  <span>14-day refund policy</span>
                </div>
                <div className="cta-feature">
                  <FiCheckCircle />
                  <span>No hidden fees</span>
                </div>
              </div>
            </div>
            <div className="cta-decoration">
              <motion.div
                className="deco-circle c1"
                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 4, repeat: Infinity }}
              />
              <motion.div
                className="deco-circle c2"
                animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
                transition={{ duration: 5, repeat: Infinity }}
              />
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
